import { useState, useRef, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { subscribeToKit, submitContact } from "@/lib/kit.functions";
import { Button } from "@/components/ui/button";

type Variant = "onNavy" | "onCream" | "onGreen";

function inputClasses(variant: Variant) {
  if (variant === "onCream") {
    return "w-full bg-white border border-navy/50 text-navy placeholder:text-navy/70 px-4 py-3 text-sm focus:border-navy";
  }
  if (variant === "onGreen") {
    return "w-full bg-warm-white/8 border border-warm-white/40 text-warm-white placeholder:text-warm-white/75 px-4 py-3 text-sm focus:border-gold";
  }
  return "w-full bg-white/5 border border-warm-white/40 text-foreground placeholder:text-warm-white/75 px-4 py-3 text-sm focus:border-green";
}

function buttonClass(variant: Variant) {
  if (variant === "onCream") return "btn-navy";
  if (variant === "onGreen") return "btn-navy";
  return "btn-primary";
}

function noteClass(variant: Variant) {
  if (variant === "onCream") return "text-navy/70";
  if (variant === "onGreen") return "text-warm-white/85";
  return "text-foreground/70";
}

export function WaitlistForm({
  tag,
  ctaLabel = "Join",
  placeholder = "your@email.com",
  variant = "onNavy",
  successMessage = "You're on the list. Watch your inbox.",
  sourcePage,
}: {
  tag: string;
  /** Route this signup came from, stored with the submission for attribution. */
  sourcePage?: string;
  ctaLabel?: string;
  placeholder?: string;
  variant?: Variant;
  successMessage?: string;
}) {
  const subscribe = useServerFn(subscribeToKit);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [devMode, setDevMode] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setError(null);
    try {
      const result = await subscribe({
        data: {
          email,
          tag,
          ...(sourcePage
            ? { sourcePage }
            : typeof window !== "undefined"
              ? { sourcePage: window.location.pathname }
              : {}),
        },
      });
      setDevMode("devMode" in result && result.devMode === true);
      setStatus("success");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong");
    }
  }

  if (status === "success") {
    return (
      <div role="status" className={"text-sm font-semibold leading-relaxed " + noteClass(variant)}>
        {devMode ? "This form is in development mode. You were not added to the mailing list." : `✓ ${successMessage}`}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row sm:flex-wrap gap-3 max-w-md">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={placeholder}
        aria-label="Email address"
        maxLength={255}
        className={inputClasses(variant) + " min-w-0 flex-1"}
      />
      <Button
        type="submit"
        disabled={status === "loading"}
        className={"btn-base h-auto whitespace-normal " + buttonClass(variant) + " disabled:opacity-60"}
      >
        {status === "loading" ? "Joining…" : ctaLabel}
      </Button>
      {error && (
        <p role="alert" className="text-xs text-destructive sm:basis-full">{error}</p>
      )}
    </form>
  );
}

export function ContactForm({ variant = "onNavy" as Variant }: { variant?: Variant } = {}) {
  const send = useServerFn(submitContact);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const mountedAtRef = useRef<number>(Date.now());
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setError(null);
    try {
      await send({
        data: { ...form, honeypot, submittedAt: mountedAtRef.current },
      });
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong");
    }
  }

  const onLight = variant === "onCream" || variant === "onGreen";

  if (status === "success") {
    return (
      <div className={onLight ? "bg-navy/5 border border-navy/30 p-6 text-center" : "bg-green/10 border border-green/40 p-6 text-center"}>
        <p className={"font-display font-bold uppercase tracking-wider text-lg " + (onLight ? "text-navy" : "text-gold")}>
          ✓ Message received
        </p>
        <p className={"text-sm mt-2 " + (onLight ? "text-navy/75" : "text-foreground/80")}>
          Carlai will be in touch soon.
        </p>
      </div>
    );
  }

  const v: Variant = variant;
  return (
    <form onSubmit={onSubmit} className="space-y-4 max-w-xl mx-auto">
      {/* Honeypot - hidden from humans, bots tend to fill it. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-10000px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label>
          Leave this field empty
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>
      <input
        type="text"
        required
        maxLength={100}
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Your name"
        aria-label="Your name"
        className={inputClasses(v)}
      />
      <input
        type="email"
        required
        maxLength={255}
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder="your@email.com"
        aria-label="Email address"
        className={inputClasses(v)}
      />
      <textarea
        required
        rows={5}
        maxLength={2000}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        placeholder="Tell us how we can help…"
        aria-label="Message"
        className={inputClasses(v) + " resize-y"}
      />
      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={status === "loading"}
          className={"btn-accent disabled:opacity-60"}
        >
          {status === "loading" ? "Sending…" : "Send Message"}
        </button>
        {error && <p className={"text-xs " + (onLight ? "text-destructive" : "text-destructive")}>{error}</p>}
      </div>
    </form>
  );
}
